import { cn } from '@/lib/cn'

/**
 * Shared look of every text-like control (inputs, selects, date fields). The
 * field is white while it can be edited, empty or filled. One that cannot is
 * gray: disabled (muted text), or a read-only input / textarea (a derived or
 * locked value, text kept readable). `:read-only` is matched on inputs and
 * textareas only — every <button> (the pickers) is read-only too.
 */
export const controlClass = cn(
  'w-full min-w-0 rounded-[var(--radius-control)] border border-field-border bg-field px-3 text-[length:calc(14px*var(--text-scale)*var(--ui-size))] text-fg',
  'shadow-[inset_0_1px_1px_rgba(0,0,0,0.03)] outline-none backdrop-blur-sm',
  'transition-[border-color,box-shadow,background-color] duration-[var(--dur-base)]',
  'hover:border-line-strong focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:shadow-[0_0_0_3px_var(--danger-soft)]',
  'disabled:cursor-not-allowed disabled:bg-field-disabled disabled:text-muted disabled:shadow-none disabled:hover:border-field-border',
  '[&:is(input,textarea):read-only]:bg-field-disabled [&:is(input,textarea):read-only]:shadow-none [&:is(input,textarea):read-only]:hover:border-field-border',
)

/**
 * Pickers — Select, RemoteSelect, MultiSelect, DateInput — share the field
 * look (gray when disabled or read-only); a chosen value reads in medium
 * weight, a disabled or read-only one in muted text. `:read-only` matches every <button>, so only a real
 * read-only input passes `readOnly`.
 */
export const pickerClass = (filled: boolean, readOnly = false) =>
  cn(controlClass, filled && 'font-medium', readOnly && 'read-only:font-normal read-only:text-muted', 'disabled:font-normal')
